import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import AleaInput from '../../components/AleaInput/AleaInput';
import AleaTelephone from '../../components/AleaTelephone/AleaTelephone';
import checkIcon from '/assets/images/icons/check.svg';
import modalCloseIcon from '/assets/images/icons/modal-close.svg';
import { useTranslation } from 'react-i18next';
import {
  accountSettingsModalAvatarStyles,
  accountSettingsModalButtonStyles,
  accountSettingsModalCloseButtonStyles,
  accountSettingsModalDeleteButtonStyles,
  accountSettingsModalSubmitButtonStyles,
  accountSettingsModalTabStyles,
  accountSettingsModalUploadAvatarStyles,
  accountSettingsModalUploadStyles,
} from './AccountSettingsModalStyles';
import AccountService from '../../services/AccountService.ts';
import { useAuth } from '../../context/AuthContext.tsx';
import AleaLoader from '../../components/AleaLoader/AleaLoader.tsx';
import AuthService from '../../services/AuthService.ts';
import AleaDropdown from '../../components/AleaDropdown/AleaDropdown.tsx';
import { positionOptions } from '../../constants/positionOptions.ts';
import { titleOptions } from '../../constants/titleOptions.ts';

interface AccountSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

interface GeneralFormValues {
  title?: string;
  firstName: string;
  lastName: string;
  position: string;
  compoundPhoneNumber: { code: string; phone: string };
  compoundMobilePhoneNumber?: {
    code?: string | null;
    phone?: string | null;
  } | null;
  emailAddress: string;
}

interface PasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const AccountSettingsModal: React.FC<AccountSettingsModalProps> = ({
  isOpen,
  onClose,
  onSaved,
}) => {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const toast = useToast();
  const [avatar, setAvatar] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAccountLoaded, setIsAccountLoaded] = useState(false);

  // Ensure header/backdrop z-index behaves correctly while modal is open,
  // regardless of where this modal is used in the app tree.
  useEffect(() => {
    try {
      const ev = new CustomEvent('alea-global-modal-open', { detail: isOpen });
      window.dispatchEvent(ev);
    } catch {}
    return () => {
      try {
        const ev = new CustomEvent('alea-global-modal-open', { detail: false });
        window.dispatchEvent(ev);
      } catch {}
    };
  }, [isOpen]);

  const generalSchema = yup.object().shape({
    title: yup.string().optional(),
    firstName: yup
      .string()
      .required(t('requiredField', { field: t('firstName') })),
    lastName: yup
      .string()
      .required(t('requiredField', { field: t('lastName') })),
    position: yup
      .string()
      .required(t('requiredField', { field: t('role') })),
    compoundPhoneNumber: yup
      .object()
      .shape({
        code: yup
          .string()
          .required(t('requiredField', { field: t('countryCode') })),
        phone: yup
          .string()
          .required(t('requiredField', { field: t('telephoneNo') })),
      })
      .required(t('requiredField', { field: t('telephoneNo') })),
    compoundMobilePhoneNumber: yup
      .object({
        code: yup.string().nullable(),
        phone: yup.string().nullable(),
      })
      .nullable()
      .notRequired(),
    emailAddress: yup
      .string()
      .email('Invalid email')
      .required(t('requiredField', { field: t('emailAddress') })),
  });

  const passwordSchema = yup.object().shape({
    currentPassword: yup
      .string()
      .required(t('requiredField', { field: t('currentPassword') })),
    newPassword: yup
      .string()
      .min(6, t('accountSettings.passwordMinLength', { min: 6 }))
      .required(t('requiredField', { field: t('newPassword') })),
    confirmPassword: yup
      .string()
      .oneOf(
        [yup.ref('newPassword'), undefined],
        t('accountSettings.passwordsMustMatch')
      )
      .required(t('requiredField', { field: t('confirmPassword') })),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset: resetGeneralForm,
  } = useForm<GeneralFormValues>({
    resolver: yupResolver(generalSchema),
    mode: 'onChange',
  });

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: errorsPassword },
    setValue: setPasswordValue,
    watch: watchPassword,
    reset: resetPasswordForm,
  } = useForm<PasswordFormValues>({
    resolver: yupResolver(passwordSchema),
    mode: 'onChange',
  });

  const accountTitle = watch('title');

  useEffect(() => {
    if (!isOpen) {
      resetGeneralForm();
      resetPasswordForm();
      setIsAccountLoaded(false);
      return;
    }

    // Fetch account details when modal is opened
    if (user?.id) {
      setIsAccountLoaded(false);
      AccountService.getAccount(user.id).then((account) => {
        setValue('firstName', account.first_name);
        setValue('lastName', account.last_name);

        const currentTitle =
          account.title && titleOptions.indexOf(account.title) !== -1
            ? account.title
            : titleOptions[0];
        setValue('title', currentTitle);

        const currentPosition =
          account.position && positionOptions.indexOf(account.position) !== -1
            ? account.position
            : positionOptions[0];
        setValue('position', currentPosition);

        const hasLandlineCode = !!account.compound_phone_number?.code;
        setValue('compoundPhoneNumber', {
          code: hasLandlineCode
            ? account.compound_phone_number!.code!
            : '+49',
          phone: hasLandlineCode
            ? account.compound_phone_number!.phone || ''
            : '',
        });

        const hasMobileCode = !!account.compound_mobile_phone_number?.code;
        setValue('compoundMobilePhoneNumber', {
          code: hasMobileCode
            ? account.compound_mobile_phone_number!.code!
            : '+49',
          phone: hasMobileCode
            ? account.compound_mobile_phone_number!.phone || ''
            : '',
        });

        setValue('emailAddress', account.email);
        setAvatar(account.image || '');
        setIsAccountLoaded(true);
      });
    } else {
      // No user id – allow form to render empty
      setIsAccountLoaded(true);
    }
  }, [isOpen, resetGeneralForm, resetPasswordForm, setValue, user, toast]);

  /**
   * Handles the submission of the general settings form
   * @param {GeneralFormValues} data - The form data
   */
  const onSubmitGeneral: SubmitHandler<GeneralFormValues> = async (data) => {
    const initialValues = {
      title: user?.title || '',
      firstName: user?.first_name || '',
      lastName: user?.last_name || '',
      position: user?.position || '',
      compoundPhoneNumber: user?.compound_phone_number || '',
      compoundMobilePhoneNumber: user?.compound_mobile_phone_number || null,
      emailAddress: user?.email || '',
    };

    if (JSON.stringify(data) === JSON.stringify(initialValues)) {
      onClose();
      return;
    }

    if (user?.id) {
      try {
        setIsLoading(true);

        const mobile = data.compoundMobilePhoneNumber;
        const payloadMobile =
          !mobile ||
          ((!mobile.code || mobile.code === '') &&
            (!mobile.phone || mobile.phone === ''))
            ? null
            : {
                code: mobile.code ?? '',
                phone: mobile.phone ?? '',
              };

        await AccountService.updateAccount(user.id, {
          title: data.title,
          first_name: data.firstName,
          last_name: data.lastName,
          position: data.position,
          compound_phone_number: data.compoundPhoneNumber,
          compound_mobile_phone_number: payloadMobile,
          email: data.emailAddress,
          username: user.username,
          role: user.role,
        });

        // After updating account, fetch updated user data
        const updatedUser = await AuthService.me();
        setUser(updatedUser);

        toast({
          title: t('success'),
          description: t('accountSettings.generalSettingsUpdatedSuccessfully'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        if (onSaved) {
          onSaved();
        }
        onClose();
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    } else {
      toast({
        title: t('error'),
        description: t('userIdNotAvailable'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /**
   * Handles the submission of the password settings form
   * @param {PasswordFormValues} data - The form data
   */
  const onSubmitPassword: SubmitHandler<PasswordFormValues> = async (data) => {
    if (user?.id) {
      try {
        setIsLoading(true);
        await AccountService.changePassword(
          user.id,
          data.currentPassword,
          data.newPassword
        );
        toast({
          title: t('success'),
          description: t('accountSettings.passwordChangedSuccessfully'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    } else {
      toast({
        title: t('error'),
        description: t('userIdNotAvailable'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /**
   * Handles the change of the profile picture file input
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event
   */
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setAvatar(reader.result as string);
      };
      reader.readAsDataURL(file);

      try {
        if (user?.id) {
          setIsLoading(true);
          await AccountService.updateAvatar(user.id, file);
          const updatedUser = await AuthService.me();
          setUser(updatedUser);
        } else {
          toast({
            title: t('error'),
            description: t('userIdNotAvailable'),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }

        toast({
          title: t('success'),
          description: t('accountSettings.profilePhotoUpdatedSuccessfully'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }

      e.target.value = '';
    }
  };

  /**
   * Handles the deletion of the profile picture
   */
  const handleDeleteAvatar = async () => {
    try {
      if (user?.id) {
        setIsLoading(true);
        await AccountService.deleteAvatar(user.id);
        setAvatar('');
        const updatedUser = await AuthService.me();
        setUser(updatedUser);
        toast({
          title: t('success'),
          description: t('accountSettings.profilePhotoDeletedSuccessfully'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: t('error'),
          description: t('userIdNotAvailable'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles tab changes and resets the corresponding form
   * @param {number} index - The index of the selected tab
   */
  const handleTabChange = (index: number) => {
    if (index === 2) {
      resetPasswordForm();
    }
  };

  const isMobile = useBreakpointValue({ base: true, md: false });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" data-testid="account-settings-modal">
      <ModalOverlay />
      <ModalContent maxWidth="1050px" p="50px" bg="#F3F2F2" data-testid="account-settings-modal-content">
        <Image
          src={modalCloseIcon}
          onClick={onClose}
          sx={accountSettingsModalCloseButtonStyles}
          data-testid="account-settings-modal-close"
          cursor="pointer"
          role="button"
        />
        <ModalHeader p={0} mb="45px">
          <Flex alignItems="center">
            {avatar && (
              <Avatar
                name={`${user?.first_name} ${user?.last_name}`}
                src={avatar || undefined}
                {...accountSettingsModalAvatarStyles}
              />
            )}

            {!avatar && (
              <Avatar
                name={`${user?.first_name} ${user?.last_name}`}
                {...accountSettingsModalAvatarStyles}
              />
            )}
            <Box>
              <Text fontSize="25px" fontWeight="500">
                {user?.first_name} {user?.last_name}
              </Text>
              <Text fontSize="15px" fontWeight={300} color="alea.500">
                {user?.position && t(user?.position)}
              </Text>
            </Box>
          </Flex>
        </ModalHeader>
        <ModalBody p={0}>
          <Tabs
            variant="soft-rounded"
            colorScheme="blue"
            orientation={isMobile ? 'horizontal' : 'vertical'}
            onChange={handleTabChange} // Handle tab change
          >
            <Flex w="100%" direction={isMobile ? 'column' : 'row'}>
              <TabList
                mr="50px"
                bg="white"
                borderRadius="20px"
                boxShadow="sm"
                w="200px"
                minW="200px"
                maxW="200px"
                alignSelf="flex-start"
              >
                <Tab {...accountSettingsModalTabStyles} data-testid="account-settings-tab-general">{t('general')}</Tab>
                <Tab {...accountSettingsModalTabStyles} data-testid="account-settings-tab-profile">{t('profilePic')}</Tab>
                <Tab {...accountSettingsModalTabStyles} data-testid="account-settings-tab-password">{t('password')}</Tab>
              </TabList>

              <TabPanels p={0}>
                {/* General content */}
                <TabPanel p={0}>
                  <form onSubmit={handleSubmit(onSubmitGeneral)}>
                    {/* Form */}
                    <Flex direction="column">
                      {/* Title Dropdown */}
                      <Box width="176px" mb="30px">
                        <AleaDropdown
                          label={t('title')}
                          options={titleOptions.map((option) => t(option))}
                          value={
                            accountTitle ? t(accountTitle) : t(titleOptions[0])
                          }
                          onChange={(translatedValue) => {
                            const originalKey = titleOptions.find(
                              (option) => t(option) === translatedValue
                            );
                            setValue('title', originalKey || titleOptions[0]);
                          }}
                        />
                      </Box>

                      <Flex mb="30px">
                        <Box flex="1" mr="30px">
                          <AleaInput
                            label={t('firstName')}
                            value={watch('firstName') || ''}
                            onChange={(e) =>
                              setValue('firstName', e.target.value)
                            }
                            register={register('firstName')}
                            error={errors.firstName?.message}
                            isRequired
                          />
                        </Box>
                        <Box flex="1">
                          <AleaInput
                            label={t('lastName')}
                            value={watch('lastName') || ''}
                            onChange={(e) =>
                              setValue('lastName', e.target.value)
                            }
                            register={register('lastName')}
                            error={errors.lastName?.message}
                            isRequired
                          />
                        </Box>
                      </Flex>

                      <Flex mb="30px">
                        <Box flex="1">
                          <AleaDropdown
                            label={t('role')}
                            options={positionOptions.map((option) => t(option))}
                            value={
                              watch('position')
                                ? t(watch('position'))
                                : t(positionOptions[0])
                            }
                            onChange={(translatedValue) => {
                              const originalKey = positionOptions.find(
                                (option) => t(option) === translatedValue
                              );
                              setValue(
                                'position',
                                originalKey || positionOptions[0]
                              );
                            }}
                            isRequired
                          />
                        </Box>
                      </Flex>

                      {/* Email on its own row */}
                      <Box mb="30px">
                        <Text
                          fontWeight={300}
                          mb="3px"
                          color="alea.500"
                          pl="16px"
                        >
                          {t('emailAddress')}
                        </Text>
                        <Text
                          fontSize="18px"
                          color="#000000"
                          fontWeight={400}
                          pl="16px"
                        >
                          {watch('emailAddress') || ''}
                        </Text>
                      </Box>

                      {/* Phone Numbers (same as setup modal) */}
                      <Flex mb="30px">
                        <Box flex="1" mr="30px">
                          <Skeleton isLoaded={isAccountLoaded} borderRadius="20px">
                            <AleaTelephone
                              label={t('telLandline')}
                              value={{
                                code: watch('compoundPhoneNumber')?.code ?? '',
                                phone:
                                  watch('compoundPhoneNumber')?.phone ?? '',
                              }}
                              onChange={(value) =>
                                setValue('compoundPhoneNumber', value)
                              }
                              register={register('compoundPhoneNumber')}
                              error={errors.compoundPhoneNumber?.phone?.message}
                              isRequired
                            />
                          </Skeleton>
                        </Box>
                        <Box flex="1">
                          <Skeleton isLoaded={isAccountLoaded} borderRadius="20px">
                            <AleaTelephone
                              label={t('telMobile')}
                              value={{
                                code:
                                  watch('compoundMobilePhoneNumber')?.code ??
                                  '',
                                phone:
                                  watch('compoundMobilePhoneNumber')?.phone ??
                                  '',
                              }}
                              onChange={(value) =>
                                setValue('compoundMobilePhoneNumber', value)
                              }
                              register={register('compoundMobilePhoneNumber')}
                              error={
                                errors.compoundMobilePhoneNumber?.message ||
                                errors.compoundMobilePhoneNumber?.phone?.message
                              }
                            />
                          </Skeleton>
                        </Box>
                      </Flex>

                      <Flex justifyContent="flex-end" mt="50px">
                        <Button
                          type="submit"
                          data-testid="account-settings-general-submit"
                          sx={accountSettingsModalSubmitButtonStyles}
                        >
                          <Image src={checkIcon} alt="Check Icon" />
                        </Button>
                      </Flex>
                    </Flex>
                  </form>
                </TabPanel>

                {/* Profile picture content */}
                <TabPanel p={0}>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                    }}
                  >
                    <Flex
                      {...accountSettingsModalUploadStyles}
                      direction={isMobile ? 'column' : 'row'}
                    >
                      {avatar && (
                        <Avatar
                          name={`${user?.first_name} ${user?.last_name}`}
                          src={avatar || undefined}
                          {...accountSettingsModalUploadAvatarStyles}
                          mb={isMobile ? '10px' : 0}
                        />
                      )}

                      {!avatar && (
                        <Avatar
                          name={`${user?.first_name} ${user?.last_name}`}
                          {...accountSettingsModalUploadAvatarStyles}
                          mb={isMobile ? '10px' : 0}
                        />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="avatarUpload"
                        onChange={handleFileChange}
                      />
                      <Flex direction="column" rowGap="15px" alignItems="flex-start">
                        <label htmlFor="avatarUpload">
                          <Button
                            as="span"
                            data-testid="account-settings-upload-avatar"
                            {...accountSettingsModalButtonStyles}
                            bg="white"
                            cursor="pointer"
                          >
                            {t('newProfilePic_button')}
                          </Button>
                        </label>

                        {avatar && (
                          <Button
                            data-testid="account-settings-delete-avatar"
                            {...accountSettingsModalDeleteButtonStyles}
                            onClick={handleDeleteAvatar}
                            bg="white"
                          >
                            {t('deleteProfilePic_button')}
                          </Button>
                        )}
                      </Flex>
                    </Flex>
                  </form>
                </TabPanel>

                {/* Password content */}
                <TabPanel p={0}>
                  <form onSubmit={handleSubmitPassword(onSubmitPassword)}>
                    <Flex flexFlow="column">
                      <Box mb="30px">
                        <AleaInput
                          label={t('currentPassword')}
                          type="password"
                          value={watchPassword('currentPassword') || ''}
                          onChange={(e) =>
                            setPasswordValue('currentPassword', e.target.value)
                          }
                          register={registerPassword('currentPassword')}
                          error={errorsPassword.currentPassword?.message}
                        />
                      </Box>

                      <Box mb="30px">
                        <AleaInput
                          label={t('newPassword')}
                          type="password"
                          value={watchPassword('newPassword') || ''}
                          onChange={(e) =>
                            setPasswordValue('newPassword', e.target.value)
                          }
                          register={registerPassword('newPassword')}
                          error={errorsPassword.newPassword?.message}
                        />
                      </Box>

                      <Box>
                        <AleaInput
                          label={t('newPasswordRepeat')}
                          type="password"
                          value={watchPassword('confirmPassword') || ''}
                          onChange={(e) =>
                            setPasswordValue('confirmPassword', e.target.value)
                          }
                          register={registerPassword('confirmPassword')}
                          error={errorsPassword.confirmPassword?.message}
                        />
                      </Box>
                      <Flex justifyContent="flex-end" mt="50px">
                        <Button
                          type="submit"
                          data-testid="account-settings-password-submit"
                          sx={accountSettingsModalSubmitButtonStyles}
                        >
                          <Image src={checkIcon} alt="Check Icon" />
                        </Button>
                      </Flex>
                    </Flex>
                  </form>
                </TabPanel>
              </TabPanels>
            </Flex>
          </Tabs>
        </ModalBody>
      </ModalContent>

      {/*loader*/}
      {isLoading && <AleaLoader />}
    </Modal>
  );
};

export default AccountSettingsModal;
